import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../../../supabaseClient'

const ALL_KNOWN_TABLES = [
  // Core settings
  'users', 'lgu_departments', 'department_systems',
  // Water billing
  'customers', 'customer_type', 'discount', 'barangays', 'bills', 'surcharge_settings',
  // Payments helpers
  // (covered by customers/bills above)
  // RPT
  'rpt_owners','rpt_properties','rpt_current_assessments','rpt_property_assessments',
  'rpt_assessments','rpt_payments','rpt_settings','rpt_tax_rates','rpt_property_kinds',
  'rpt_property_classifications','rpt_amnesty',
  // MCR
  'mcr_categories','mcr_subcategories','mcr_services','mcr_clients','mcr_orders','mcr_order_items','mcr_access_codes',
  // RHU
  'rhu_categories','rhu_subcategories','rhu_services','rhu_patients','rhu_orders','rhu_order_items','rhu_access_codes',
  // e-LogBook
  'elogbook_transactions','for_advise',
  // Views (optional; will be empty if not exposed)
  'logbook_entries'
]

const defaultTables = [...ALL_KNOWN_TABLES]

const OfflineBackup = ({ user }) => {
  const [tables, setTables] = useState(defaultTables)
  const [customTable, setCustomTable] = useState('')
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState('')
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)

  const addCustom = () => {
    const t = customTable.trim()
    if (!t) return
    if (!tables.includes(t)) setTables([...tables, t])
    setCustomTable('')
  }

  const removeTable = (t) => setTables(tables.filter(x => x !== t))

  const exportSelected = async (tablesToExport = tables) => {
    setLoading(true)
    setStatus('')
    setError('')
    try {
      const snapshot = { exported_at: new Date().toISOString(), by: user ? `${user.firstname} ${user.lastname}` : null, data: {} }
      for (const t of tablesToExport) {
        const { data, error: err } = await supabase.from(t).select('*')
        if (err) {
          snapshot.data[t] = { error: err.message, rows: [] }
        } else {
          snapshot.data[t] = { rows: data || [] }
        }
      }
      const json = JSON.stringify(snapshot, null, 2)
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `offline_snapshot_${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setResult({ success: true })
      setStatus('Snapshot exported successfully')
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const backupAllAndEnableOffline = async () => {
    await exportSelected(ALL_KNOWN_TABLES)
    try {
      const text = localStorage.getItem('offline_snapshot')
      if (!text) {
        // If importSnapshot hasn't been used, also store the last downloaded content as offline
        // Recreate snapshot and store locally
        const snapshot = { exported_at: new Date().toISOString(), by: user ? `${user.firstname} ${user.lastname}` : null, data: {} }
        for (const t of ALL_KNOWN_TABLES) {
          const { data, error: err } = await supabase.from(t).select('*')
          snapshot.data[t] = err ? { error: err.message, rows: [] } : { rows: data || [] }
        }
        localStorage.setItem('offline_snapshot', JSON.stringify(snapshot))
      }
      localStorage.setItem('offline_mode', 'true')
      setStatus('Full backup completed. Offline mode enabled.')
    } catch (e) {
      setError('Failed to enable offline after backup: ' + e.message)
    }
  }

  const importSnapshot = async (file) => {
    setLoading(true)
    setStatus('')
    setError('')
    try {
      const text = await file.text()
      const json = JSON.parse(text)
      localStorage.setItem('offline_snapshot', JSON.stringify(json))
      setResult({ success: true })
      setStatus('Snapshot imported into local storage')
    } catch (e) {
      setError('Failed to import: ' + e.message)
    } finally {
      setLoading(false)
    }
  }

  const enableOfflineMode = () => {
    localStorage.setItem('offline_mode', 'true')
    setStatus('Offline mode enabled')
  }

  const disableOfflineMode = () => {
    localStorage.removeItem('offline_mode')
    setStatus('Offline mode disabled')
  }

  const offlineMode = useMemo(() => localStorage.getItem('offline_mode') === 'true', [])

  return (
    <div className="settings-content">
      <div className="settings-header">
        <h1>Offline Backup</h1>
        <p>Export Supabase tables to JSON for offline use, and import snapshots into local storage.</p>
      </div>

      <div className="settings-section">
        <h2>Tables to Export</h2>
        <div className="field" style={{ display: 'flex', gap: '0.5rem', maxWidth: '600px' }}>
          <input className="input" type="text" placeholder="Add table name" value={customTable} onChange={(e)=>setCustomTable(e.target.value)} />
          <button className="button is-primary" onClick={addCustom}><span className="icon"><i className="fas fa-plus"></i></span><span>Add</span></button>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
          {tables.map(t => (
            <span key={t} className="tag is-light" style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center' }}>
              {t}
              <button className="delete is-small" onClick={() => removeTable(t)}></button>
            </span>
          ))}
        </div>
        <div style={{ marginTop: '0.75rem' }}>
          <button className={`button ${loading ? 'is-loading' : ''} is-success`} onClick={exportSelected}>
            <span className="icon"><i className="fas fa-download"></i></span>
            <span>Export Snapshot</span>
          </button>
          <button className={`button ${loading ? 'is-loading' : ''} is-warning`} style={{ marginLeft: '0.5rem' }} onClick={backupAllAndEnableOffline}>
            <span className="icon"><i className="fas fa-database"></i></span>
            <span>Backup ALL & Enable Offline</span>
          </button>
        </div>
      </div>

      <div className="settings-section">
        <h2>Import Snapshot</h2>
        <div>
          <input type="file" accept="application/json" onChange={(e)=>e.target.files && e.target.files[0] && importSnapshot(e.target.files[0])} />
        </div>
      </div>

      <div className="settings-section">
        <h2>Offline Mode</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="button" onClick={enableOfflineMode}><span className="icon"><i className="fas fa-toggle-on"></i></span><span>Enable</span></button>
          <button className="button" onClick={disableOfflineMode}><span className="icon"><i className="fas fa-toggle-off"></i></span><span>Disable</span></button>
        </div>
        <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>Modules can read `offline_snapshot` when `offline_mode` is enabled.</p>
      </div>

      {(status || error) && (
        <div className="settings-section">
          {status && <div className="notification is-success">{status}</div>}
          {error && <div className="notification is-danger">{error}</div>}
        </div>
      )}
    </div>
  )
}

export default OfflineBackup
